import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

/*
  TYPES
*/

interface AllowListModalContext {
  allowListModalOpen: boolean;
  openAllowListModal: () => void;
  closeAllowListModal: () => void;
}

interface ConfirmUnsavedChangesModalContext {
  confirmUnsavedChangesModalOpen: boolean;
  openConfirmUnsavedChangesModal: () => void;
  closeConfirmUnsavedChangesModal: () => void;
  setNextAction: (action: () => void) => void;
  nextAction: () => void;
}

export type CampaignWorkflowContext = AllowListModalContext & ConfirmUnsavedChangesModalContext;

/*
  CONTEXT
*/

const Context = createContext<CampaignWorkflowContext>(null);

/*
  INTERNAL HOOKS
*/

const useAllowListModalContext = (): AllowListModalContext => {
  const [allowListModalOpen, setAllowListModalOpen] = useState(false);
  const openAllowListModal = useCallback(() => setAllowListModalOpen(true), []);
  const closeAllowListModal = useCallback(() => setAllowListModalOpen(false), []);
  return {
    allowListModalOpen,
    openAllowListModal,
    closeAllowListModal,
  };
};

const useConfirmUnsavedChangesModalContext = (): ConfirmUnsavedChangesModalContext => {
  const [confirmUnsavedChangesModalOpen, setConfirmUnsavedChangesModalOpen] = useState(false);
  const openConfirmUnsavedChangesModal = useCallback(() => setConfirmUnsavedChangesModalOpen(true), []);
  const closeConfirmUnsavedChangesModal = useCallback(() => setConfirmUnsavedChangesModalOpen(false), []);

  const [nextAction, setNextAction] = useState<() => void>(() => ({}));
  const handleSetNextAction = useCallback((action: () => void) => {
    setNextAction(() => action);
  }, []);

  return {
    confirmUnsavedChangesModalOpen,
    openConfirmUnsavedChangesModal,
    closeConfirmUnsavedChangesModal,
    nextAction,
    setNextAction: handleSetNextAction,
  };
};

/*
  PROVIDER
*/

export function CampaignWorkflowProvider({ children }: PropsWithChildren<any>) {
  const allowListModalContext = useAllowListModalContext();
  const confirmUnsavedChangesModalContext = useConfirmUnsavedChangesModalContext();

  const context: CampaignWorkflowContext = useMemo(
    () => ({
      ...allowListModalContext,
      ...confirmUnsavedChangesModalContext,
    }),
    [allowListModalContext, confirmUnsavedChangesModalContext],
  );

  return <Context.Provider value={context}>{children}</Context.Provider>;
}

/*
  EXTERNAL HOOKS
*/

export const useCampaignWorkflow = (): CampaignWorkflowContext => {
  const contextValues = useContext(Context);
  if (!contextValues) {
    throw Error('useCampaignWorkflow must be used in a CampaignWorkflowProvider');
  }

  return contextValues;
};

export const useAllowListModal = (): AllowListModalContext => {
  const contextValues = useContext(Context);
  if (!contextValues) {
    throw new Error('useAllowListModal must be used in a CampaignWorkflowProvider');
  }

  return {
    allowListModalOpen: contextValues.allowListModalOpen,
    openAllowListModal: contextValues.openAllowListModal,
    closeAllowListModal: contextValues.closeAllowListModal,
  };
};

export const useConfirmUnsavedChangesModal = (): ConfirmUnsavedChangesModalContext => {
  const contextValues = useContext(Context);
  if (!contextValues) {
    throw new Error('useConfirmUnsavedChangesModal must be used in a CampaignWorkflowProvider');
  }

  return {
    confirmUnsavedChangesModalOpen: contextValues.confirmUnsavedChangesModalOpen,
    openConfirmUnsavedChangesModal: contextValues.openConfirmUnsavedChangesModal,
    closeConfirmUnsavedChangesModal: contextValues.closeConfirmUnsavedChangesModal,
    nextAction: contextValues.nextAction,
    setNextAction: contextValues.setNextAction,
  };
};
